<template>
  <Circle ref="elementRef"
          :options="circleOptions"
          @click="onClick"
          @center_changed="onCenterChange"
          @radius_changed="onRadiusChange"
          @mouseover="onHover"
          @mouseout="onHoverExit"
  >
  </Circle>
  <Marker v-if="showGeoFenceName" :options="markerNameOptions"></Marker>
  <InfoWindow v-if="isInfoWindowShown"
              ref="infoWindowRef"
              :options="infoWindowOptions"
              @closeclick="onInfoWindowCloseClick">
    <GeoFenceInfo :title="infoWindowOptions.title"
                  :description="infoWindowOptions.description"
                  :coordinates="infoWindowOptions.position"
                  :radius="infoWindowOptions.radius"
                  :allow-edit="allowEdit"
                  :is-edit-mode="isEditMode"
                  @update:title="setInfoWindowName"
                  @update:description="setInfoWindowDescription"
                  @edit="editGeoFence"
                  @cancel="cancelGeoFenceEdit"
                  @save="saveGeoFence">
    </GeoFenceInfo>
  </InfoWindow>
</template>

<script>

import {Circle, InfoWindow, Marker} from "vue3-google-map";
import {dataManipulator, GoogleMapsManager} from "@/assets/js/data-manipulation";
import GeoFenceInfo from "@/components/pages/geo-fences/GeoFenceInfo.vue";
import {actions} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";

export default {
  name: "CircleGeoFence",
  components: {GeoFenceInfo, Circle, InfoWindow, Marker},
  props: {
    geoFenceData: {
      type: Object,
      required: true
    },
    customOptions: Object,
    showName: {
      type: Boolean,
      default: false
    },
    hoverEnabled: {
      type: Boolean,
      default: true
    },
    allowEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click', 'center_changed', 'radius_changed', 'created', 'saved', 'cancel_edit_mode'],
  data() {
    return {
      infoWindowOptions: null,
      geoFenceDataChanges: dataManipulator.deepCopyObject(this.geoFenceData),
      infoWindowShownOnHover: false,
      isEditMode: false,
    }
  },
  watch: {
    geoFenceData: {
      deep: true,
      handler(newValue) {
        this.geoFenceDataChanges = dataManipulator.deepCopyObject(newValue);
      }
    }
  },
  computed: {
    id() {
      return this.data?.id;
    },
    data() {
      return this.geoFenceData
    },
    circleOptions() {
      return {
        ...GoogleMapsManager.convertUserGeoFenceToCircleOptions(this.geoFenceData),
        ...this.customOptions
      }
    },
    isInfoWindowShown() {
      return this.infoWindowOptions !== null;
    },
    showGeoFenceName() {
      return this.showName && this.geoFenceDataChanges?.name;
    },
    markerNameOptions() {
      return {
        position: this.geoFenceDataChanges?.area?.coordinates,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 0,
        },
        label: {
          className: 'map-label',
          text: this.geoFenceDataChanges?.name,
        }
      }
    }
  },
  methods: {
    onClick() {
      this.$emit('click', this);
    },
    onCenterChange() {
      this.$emit('center_changed', this);
      this.geoFenceDataChanges.area.coordinates = this.getCenter();
      if (this.isInfoWindowShown) {
        this.$refs.infoWindowRef.infoWindow.setPosition(this.geoFenceDataChanges.area.coordinates);
        this.infoWindowOptions.position = this.geoFenceDataChanges.area.coordinates;
      }
    },
    onRadiusChange() {
      this.$emit('radius_changed', this);
      this.geoFenceDataChanges.area.radius = this.getRadius();
      if (this.isInfoWindowShown) {
        this.infoWindowOptions.radius = this.geoFenceDataChanges.area.radius;
      }
    },
    onHover() {
      if (!this.hoverEnabled) {
        return;
      }

      if (this.isInfoWindowOpen()) {
        return;
      }

      this.showInfoWindow();
      this.infoWindowShownOnHover = true;
    },
    onHoverExit() {
      if (!this.hoverEnabled) {
        return;
      }

      if (!this.infoWindowShownOnHover) {
        return
      }

      this.closeInfoWindow();
    },
    setEditable() {
      GoogleMapsManager.setGeoFenceEditable(this.getInnerObject());
    },
    setReadOnly() {
      GoogleMapsManager.setGeoFenceReadOnly(this.getInnerObject());
      this.isEditMode = false;
    },
    resetOptions() {
      this.getInnerObject()?.setOptions(this.circleOptions);
      this.geoFenceDataChanges = dataManipulator.deepCopyObject(this.geoFenceData);
    },
    getCenter() {
      return GoogleMapsManager.getCoordinatesFromGoogleData(this.getInnerObject()?.getCenter());
    },
    setCenter(center) {
      this.getInnerObject()?.setCenter(center);
      this.geoFenceDataChanges.area.coordinates = center;
      if (this.isInfoWindowShown) {
        this.$refs.infoWindowRef.infoWindow.setPosition(this.geoFenceDataChanges.area.coordinates);
        this.infoWindowOptions.position = this.geoFenceDataChanges.area.coordinates;
      }
    },
    getRadius() {
      return parseInt(this.getInnerObject()?.getRadius(), 10);
    },
    setRadius(radius) {
      this.getInnerObject()?.setRadius(radius);
      this.geoFenceDataChanges.area.radius = radius;
      if (this.isInfoWindowShown) {
        this.infoWindowOptions.radius = radius;
      }
    },
    setColor(color) {
      const options = {...this.getInnerObject()?.options, ...GoogleMapsManager.getGeoFenceColorOptions(color)};
      this.getInnerObject()?.setOptions(options);
    },
    getInnerObject() {
      return this.$refs.elementRef?.circle;
    },
    showInfoWindow() {
      this.infoWindowShownOnHover = false;
      if (this.isInfoWindowShown) {
        this.$refs.infoWindowRef.open();
      }

      this.infoWindowOptions = {
        title: this.geoFenceDataChanges.name,
        description: this.geoFenceDataChanges.description,
        position: this.geoFenceDataChanges.area.coordinates,
        radius: this.geoFenceDataChanges.area.radius
      };
    },
    setInfoWindowName(name) {
      this.geoFenceDataChanges.name = name;

      if (this.isInfoWindowShown) {
        this.infoWindowOptions.title = this.geoFenceDataChanges.name;
        this.infoWindowOptions.description = this.geoFenceDataChanges.description;
      }
    },
    setInfoWindowDescription(description) {
      this.geoFenceDataChanges.description = description;

      if (this.isInfoWindowShown) {
        this.infoWindowOptions.title = this.geoFenceDataChanges.name;
        this.infoWindowOptions.description = this.geoFenceDataChanges.description;
      }
    },
    closeInfoWindow() {
      this.infoWindowShownOnHover = false;
      if (this.isEditMode) {
        this.cancelGeoFenceEdit();
      }
      this.infoWindowOptions = null;
    },
    isInfoWindowOpen() {
      return this.isInfoWindowShown && this.$refs.infoWindowRef?.infoWindow.isOpen;
    },
    onInfoWindowCloseClick() {
      this.closeInfoWindow();
    },
    editGeoFence() {
      if (!this.allowEdit) {
        return;
      }

      this.infoWindowShownOnHover = false;
      this.isEditMode = true;
      this.setEditable();
    },
    cancelGeoFenceEdit() {
      if (!this.isEditMode) {
        return;
      }

      this.geoFenceDataChanges.name = this.geoFenceData.name;
      this.geoFenceDataChanges.description = this.geoFenceData.description;
      this.geoFenceDataChanges.area = dataManipulator.deepCopyObject(this.geoFenceData.area);
      this.setCenter(this.geoFenceData.area.coordinates);
      this.setRadius(this.geoFenceData.area.radius);
      if (this.isInfoWindowShown) {
        this.infoWindowOptions.title = this.geoFenceDataChanges.name;
        this.infoWindowOptions.description = this.geoFenceDataChanges.description;
      }
      this.setReadOnly();
      this.$emit('cancel_edit_mode');
    },
    async saveGeoFence() {
      if (!this.allowEdit || !this.isEditMode) {
        return;
      }
      const geoFenceData = dataManipulator.deepCopyObject(this.geoFenceData);
      geoFenceData.name = this.geoFenceDataChanges.name;
      geoFenceData.description = this.geoFenceDataChanges.description;
      geoFenceData.area = dataManipulator.deepCopyObject(this.geoFenceDataChanges.area);
      geoFenceData.attributes = dataManipulator.deepCopyObject(this.geoFenceDataChanges.attributes);
      const isCreateMode = geoFenceData.id === 0;
      const action = isCreateMode ? actions.CREATE_USER_GEO_FENCE : actions.UPDATE_USER_GEO_FENCE;

      try {
        const result = await this.$store.dispatch(action, geoFenceData);
        await this.$store.dispatch(actions.GET_USER_GEO_FENCES);
        notification.success(this.$t('generic.save.success'));
        if (isCreateMode) {
          this.$emit('created', result);
        }
        this.$emit('saved', result?.id ? result : geoFenceData);
      }
      catch (e) {
        notification.error(this.$t(e.message));
      }
      finally {
        this.setReadOnly();
      }
    }
  }
}
</script>

<style scoped lang="scss">

</style>